import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { FormModel, Input, Select, Icon, Tooltip, Checkbox, Modal, Spin } from "ant-design-vue";
import { ContentService, CurrentContentDetailsForm } from "@/services/content";
import { cloneDeep, max, range } from "lodash";
import { CategoryResponseModel, TypeResponseModel } from "@/services/category-manager";
import { LanguageSkillModel, VCSLevelModel } from "@/models/title-list-model";
import CategoryManagerState from "@/modules/category-manager";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { fmtMsg, GLUtil, isGuid, MessageHelper, CurriculumType } from "commonui";
import { VersionGroupModule } from "@/modules/versiongroup";
import { Paths } from "@/util/paths";
import { CommonFormLocale, TitleListLocale } from "@/locales/localeid";
import { AccessLevel, LanguageSkill, ParentType, SpecialDesignation } from "@/util/enum";
import { ApplicationViews } from "@/util/enum";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { MediaSlotBinding } from "@/views/text-guardian/sub-views/title-list/sub-views/media/utils/const";

@Component({
	components: {
		Form: FormModel,
		FormItem: FormModel.Item,
		Input,
		Select,
		SelectOption: Select.Option,
		Icon,
		Tooltip,
		Checkbox,
		Spin
	}
})
export default class CommonForm extends Vue {
	@Prop() showComparison!: () => void;
	public CurrentDeails!: CurrentContentDetailsForm; // check in meta
	public versionGroupModule = VersionGroupModule;
	private categoryModule = getModule(CategoryManagerState);
	private titleListModule = getModule(TitleListState);

	public categories: CategoryResponseModel[] = []; // only pass categories which have types
	public skills: LanguageSkillModel[] = [];
	public levels: VCSLevelModel[] = []; // alias for Units
	public availableTypes: TypeResponseModel[] = [];
	public contentDetails: CurrentContentDetailsForm = {
		title: "",
		categoryId: 0,
		contentTypeId: 0,
		languageSkillId: 0,
		vcsLevelId: 0
	};
	public chapterName = "";
	public revisions: number[] = [];
	public contentDetailsProxy: CurrentContentDetailsForm = {} as CurrentContentDetailsForm;
	public fmtMsg = fmtMsg;
	public locale = TitleListLocale;
	public labelCol = { span: 8 };
	public wrapperCol = { span: 16 };
	public accessLevel = AccessLevel;
	public relatedLesson = false;
	public messRemoveConversation = fmtMsg(CommonFormLocale.CommonMessageIsParent);
	public messRemoveConversationInPlaylist = fmtMsg(CommonFormLocale.CommonMessageIsParentRelatedPlaylist);

	rules = {
		title: [{ required: true, message: fmtMsg(this.locale.TitleListCreateTitle), trigger: "blur" }]
	};

	@Watch("titleListModule.editeContentSelectedChapterId", { immediate: true })
	updateChapterName() {
		const chapterId = this.titleListModule.editeContentSelectedChapterId;
		const { editedContentDetails, editedContentId, editeContentSelectedChapterId } = this.titleListModule;
		// update revision for chapter
		if (chapterId !== "all" && chapterId !== "none" && editedContentId.length) {
			// update revisions
			const chap = editedContentDetails.chapters.find((c) => c.id == editeContentSelectedChapterId);
			this.chapterName = chap?.title as string;
		}
	}

	@Watch("titleListModule.editeContentSelectedChapterId")
	updateRevisionOnChapterChange() {
		const chapterId = this.titleListModule.editeContentSelectedChapterId;
		const { editedContentDetails, editedContentId, setRevision, isParent } = this.titleListModule;
		// update revision for chapter
		if (chapterId !== "all" && chapterId !== "none" && editedContentId.length) {
			// update revisions
			let maxRevision = max(
				editedContentDetails.chapters.find((c) => c.id === chapterId)?.contentText.map((c) => c.revision)
			);
			maxRevision = maxRevision ? maxRevision : 0;
			setRevision(maxRevision);
			this.revisions = range(0, maxRevision + 1);
		}
		if (chapterId === "all") {
			setRevision(editedContentDetails.revisions);
		}
	}

	@Watch("titleListModule.editedContentId")
	updateLevelsAfterCreation() {
		if (this.titleListModule.editedContentId.length) {
			const currentLevel = this.levels.find((l) => l.id === this.titleListModule.editedContentDetails.vcsLevelId);
			this.levels = this.levels.filter((l) => l.curriculumType === currentLevel?.curriculumType);
		}
	}

	@Watch("contentDetailsProxy.categoryId")
	@Watch("contentDetailsProxy.contentTypeId")
	updateTypes() {
		this.resetREPCM();
	}

	@Watch("titleListModule.currentRevision")
	updateRevisionList() {
		if (this.titleListModule.currentRevision > max(this.revisions)!) {
			this.revisions = range(0, this.titleListModule.currentRevision + 1);
		}
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	versionChanged() {
		this.updateLevels();
		if (this.titleListModule.editedContentId.length) {
			if (
				(this.titleListModule.editedContentDetails.curriculumType === CurriculumType.GrapeSEED &&
					!isGuid(VersionGroupModule.selectedGsVersion)) ||
				(this.titleListModule.editedContentDetails.curriculumType === CurriculumType.LittleSEED &&
					!isGuid(VersionGroupModule.selectedLsVersion))
			) {
				this.$router.replace(Paths.Components);
				this.titleListModule.resetChildState();
			}
		}
	}

	get isTextOrManageTab() {
		const currentPath = this.$route.matched.slice(-1)[0].path;
		if (currentPath === Paths.EditText || currentPath === Paths.Text || currentPath === Paths.EditManageChapters) {
			return true;
		}
		return false;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	resetREPCM() {
		const category = this.categoryModule.categories.find((c) => c.id === this.contentDetailsProxy.categoryId);

		if (category) {
			const filteredTypes = category.types.filter((t) => t.specialDesignationId === SpecialDesignation.None);
			const subCategory = filteredTypes.find((t) => t.id === this.contentDetailsProxy.contentTypeId);
			this.titleListModule.setAllowTeachingPlan(category.allowTeachingPlan);

			if (subCategory) {
				this.titleListModule.setAllowRepPlan(subCategory.allowRepPlan);
				this.titleListModule.setIsSubCategorySupportResource(subCategory.isSupportResource);
			}
		}

		if (
			!this.titleListModule.allowTeachingPlan &&
			[ApplicationViews.ContentObjective, ApplicationViews.ContentExposure].includes(this.$route.meta.viewId)
		) {
			this.$router.replace(GLUtil.pathStringify(Paths.EditText, { id: this.titleListModule.editedContentId }));
		}
	}

	handleUnitChange(value) {
		const contentDetails = { ...this.titleListModule.editedContentDetails, vcsLevelId: value };
		this.titleListModule.setContentDetails(contentDetails);
	}

	updateLevels() {
		let levels = this.titleListModule.levels;
		// Filter levels according to selected versions
		if (!isGuid(VersionGroupModule.selectedGsVersion)) {
			levels = levels.filter((l) => l.curriculumType !== CurriculumType.GrapeSEED);
		}
		if (!isGuid(VersionGroupModule.selectedLsVersion)) {
			levels = levels.filter((l) => l.curriculumType !== CurriculumType.LittleSEED);
		}
		if (!levels.length) {
			this.$router.push(Paths.Components);
			return;
		}
		this.levels = levels;
		// if not in edit mode, then update the selected level as in this.titleListModule.listSelectedLevel
		if (this.titleListModule.editedContentId.length === 0 && this.levels.length) {
			if (this.titleListModule.listSelectedLevel === 0) {
				this.contentDetailsProxy.vcsLevelId = this.levels[0].id;
			} else {
				const selectedLevel = this.levels.find((l) => l.id === this.titleListModule.listSelectedLevel)?.id;
				if (typeof selectedLevel === "number") {
					this.contentDetailsProxy.vcsLevelId = selectedLevel as number;
				} else {
					this.contentDetailsProxy.vcsLevelId = this.levels[0].id;
				}
			}
		}
	}

	mounted() {
		this.categories = this.categoryModule.categories.filter(
			(cat) => cat.types.length && !cat.disabled && !cat.isLessonManagement && cat.isCurriculum
		);
		this.availableTypes = this.categories[0].types.filter(
			(type) => !type.disabled && type.specialDesignationId === SpecialDesignation.None
		);
		this.skills = this.titleListModule.languageSkills;
		this.updateLevels();
		this.updateContentDetails(); // create form object model
	}

	beforeDestroy() {
		this.titleListModule.setValidForm(false);
		this.titleListModule.setFormTocuched(false);
	}

	async updateContentDetails() {
		while (this.titleListModule.loading) {
			await new Promise((res) => setTimeout(res, 100));
		}
		if (
			Object.keys(this.titleListModule.editedContentDetails).length &&
			this.titleListModule.editedContentId.length
		) {
			const conCopy = cloneDeep(this.titleListModule.editedContentDetails);
			const category = this.categoryModule.categories.find((c) =>
				c.types.some((c) => c.id === conCopy.contentTypeId)
			) as CategoryResponseModel;
			const currentLevel = this.levels.find((l) => l.id === conCopy.vcsLevelId);
			this.levels = this.levels.filter((l) => l.curriculumType === currentLevel?.curriculumType);
			this.availableTypes = category.types.filter(
				(t) => !t.disabled && t.specialDesignationId === SpecialDesignation.None
			);
			// update contentDetails from conCopy
			this.contentDetails = {
				title: conCopy.title,
				categoryId: category.id,
				contentTypeId: conCopy.contentTypeId,
				languageSkillId: conCopy.languageSkillId,
				vcsLevelId: conCopy.vcsLevelId
			};
			// set the appropriate revision range
			if (conCopy.chapters.length) {
				const selChap = conCopy.chapters
					.find((c) => c.id === this.titleListModule.editeContentSelectedChapterId)
					?.contentText.map((m) => m.revision);
				if (selChap) {
					this.titleListModule.setOnlyIsParent(true);
					this.revisions = range(0, (max(selChap) as number) + 1);
				} else {
					this.revisions = [0];
					if (!this.titleListModule.currentRevision) {
						this.titleListModule.setRevision(0);
					}
				}
			} else {
				this.revisions = range(0, conCopy.revisions + 1);
			}

			this.titleListModule.setAllowTeachingPlan(category.allowTeachingPlan);
			const subCategory = category.types.find((t) => t.id === conCopy.contentTypeId);

			if (subCategory) {
				this.titleListModule.setAllowRepPlan(subCategory.allowRepPlan);
			}

			this.titleListModule.setREPCM({
				category: this.contentDetails.categoryId,
				contentTypeId: conCopy.contentTypeId
			});
			this.titleListModule.setUpdatedDetails(this.contentDetails);
			this.createProxy();
		} else if (!this.titleListModule.editedContentId.length) {
			const category = this.categories[0];

			this.contentDetails = {
				title: "",
				categoryId: category.id,
				contentTypeId: category.types[0].id,
				languageSkillId: this.skills[0].id,
				vcsLevelId:
					this.titleListModule.listSelectedLevel != 0
						? this.titleListModule.listSelectedLevel
						: this.levels[0].id
			};
			this.revisions = [0]; // for new title set the revision manually to zero
			if (this.titleListModule.currentRevision !== 0) {
				this.titleListModule.setRevision(0);
			}

			this.titleListModule.setAllowTeachingPlan(category.allowTeachingPlan);
			const subCategory = category.types.find((t) => t.id === this.contentDetails.contentTypeId);

			if (subCategory) {
				this.titleListModule.setAllowRepPlan(subCategory.allowRepPlan);
			}

			this.titleListModule.setREPCM({
				category: this.contentDetails.categoryId,
				contentTypeId: this.categories[0].types[0].id
			});
			this.titleListModule.setUpdatedDetails(this.contentDetails);
			this.createProxy();
		}
	}

	createProxy() {
		// this will help in capturing the change
		this.contentDetailsProxy = new Proxy(this.contentDetails, {
			set: (target, key, value) => {
				if (key === "categoryId") {
					// when category value is being changed, update the types (sub-categories)
					const category = this.categories.find((c) => c.id === value) as CategoryResponseModel;
					this.availableTypes = category.types.filter(
						(t) => !t.disabled && t.specialDesignationId === SpecialDesignation.None
					);
					this.contentDetailsProxy.contentTypeId = this.availableTypes[0].id;
				}

				this.titleListModule.setFormTocuched(true);
				target[key] = value;
				this.titleListModule.setUpdatedDetails(this.contentDetails);
				return true;
			}
		});
	}

	get hideComparer() {
		const { editedContentId, editedContentDetails, isParent } = this.titleListModule;
		if (
			!editedContentId.length ||
			(isParent && (!editedContentDetails.chapters || !editedContentDetails.chapters.length))
		) {
			return true;
		}
		return false;
	}

	get getTitleChapters() {
		const allOption = { id: "all", title: "All" };
		if (this.titleListModule.editedContentDetails.chapters) {
			return [allOption, ...this.titleListModule.editedContentDetails.chapters];
		}
		return [allOption];
	}

	revisionChanged(rev: number) {
		const {
			setRevision,
			setRevisionChanged,
			fetchContentDetails,
			togglePageDirty,
			textPageDirty,
			editeContentSelectedChapterId
		} = this.titleListModule;

		const onLeave = () => {
			setRevision(rev);
			if (editeContentSelectedChapterId !== "none" && editeContentSelectedChapterId !== "all") {
				// chapter revision is changed, to re-render the textarea
				setRevisionChanged(true);
			} else {
				fetchContentDetails().then(() => setTimeout(() => setRevisionChanged(true), 250));
			}
		};

		if (textPageDirty) {
			MessageHelper.Confirm(fmtMsg(TitleListLocale.TitleListLeaveWarn), () => {
				togglePageDirty();
				onLeave();
			});
		} else {
			onLeave();
		}
	}

	get isParentDisabled(): boolean {
		return !this.isTextOrManageTab || !this.titleListModule.editedContentId.length || this.disabledDueToAccessLevel;
	}

	// when 'is parent' checkbox is changed
	onIsParentChange(e: HTMLInputElement & any) {
		// show warning to user everytime the checkbox is checked
		if (e.target.checked) {
			this.titleListModule.getComponentContentState(this.$route.params.id).then(() => {
				const componentState = this.titleListModule.componentContentState;
				const mediaSlots: string[] = componentState.mediaSlots
					.filter((slot) => slot.exists)
					.map((slot) => fmtMsg(MediaSlotBinding[slot.mediaSlotId]));
				const hasMediaSlots = !!mediaSlots.length;
				const hasText = this.titleListModule.hasTextDetails;
				const hasContent = hasText || hasMediaSlots || componentState.hasConversation;

				if (!hasContent) {
					this.titleListModule.setFormTocuched(true);
					this.titleListModule.setCheckboxParent(true);
					return;
				}
				const createVNode = (h, tag: string, content?: string | null, children?: Array<unknown>) => {
					return h(
						tag,
						content
							? {
								domProps: {
									innerHTML: content
								}
							}
							: null,
						children
					);
				};
				const message = (h) => {
					return createVNode(h, "div", "", [
						// createVNode(h, "div", fmtMsg(CommonFormLocale.CommonMessageIsParentConfirmOnChanging)),
						createVNode(h, "ul", null, [
							hasText && createVNode(h, "li", fmtMsg(CommonFormLocale.CommonMessageIsParentConfirmText)),
							hasMediaSlots &&
							createVNode(h, "li", fmtMsg(CommonFormLocale.CommonMessageIsParentConfirmMedia)),
							hasMediaSlots &&
							createVNode(
								h,
								"ul",
								null,
								mediaSlots.map((slot) => createVNode(h, "li", slot))
							),
							componentState.hasConversation &&
							createVNode(h, "li", fmtMsg(CommonFormLocale.CommonMessageIsParentConfirmConversation))
						]),
						createVNode(h, "div", fmtMsg(CommonFormLocale.CommonMessageIsParentConfirmAreYouSure))
					]);
				};
				Modal.confirm({
					onOk: async () => {
						this.titleListModule.setFormTocuched(true);
						this.titleListModule.setCheckboxParent(true);
					},
					title: fmtMsg(CommonFormLocale.CommonMessageIsParentConfirmOnChanging),
					content: message,
					width: 600
				});
			});
		} else {
			Modal.confirm({
				onOk: async () => {
					this.titleListModule.setFormTocuched(false);
					this.titleListModule.setCheckboxParent(false);

					await ContentService.UpdateContentParentType(
						this.titleListModule.editedContentId,
						ParentType.Normal
					);
					this.titleListModule.textPageDirty && this.titleListModule.togglePageDirty();
					this.titleListModule.setIsParent(false);
					this.titleListModule.setRevision(-1);
					await this.titleListModule.fetchContentDetails(this.titleListModule.editedContentId);
					const currentPath = this.$route.matched.slice(-1)[0].path;

					if (currentPath === Paths.ManageChapters || currentPath === Paths.EditManageChapters) {
						await this.$router.replace(GLUtil.pathStringify(Paths.EditText, { id: this.$route.params.id }));
					}
				},
				title: "Are you sure you want to remove this title as parent?"
			});
		}

		// Prevented bubbling and default behaviour because action needs to be taken on user confirmation
		// So checkbox state needs to be toggled only when user confirms, hence handler executes on
		// click event which executes before change event.
		e.preventDefault();
		e.stopPropagation();
		return false;
	}

	// When in edit mode and title's chapter is selected
	onChapterSelected(chapterId: string) {
		// try to navigate to manage chapter screen if 'all' option is selected
		const currentPath = this.$route.matched.slice(-1)[0].path;
		if (chapterId === "all" && this.titleListModule.isParent) {
			if (this.$route.matched.slice(-1)[0].path === Paths.EditText) {
				// update the possible update in titleListModule, but final decision would be taken in 'text.ts' beforeRouteLeave
				// as we need to show the warning on leave.
				this.titleListModule.setPossibleEditeContentSelectedChapterId(chapterId);
			} else {
				this.titleListModule.setEditeContentSelectedChapterId(chapterId);
			}

			// redirect to 'EditManageChapters' route
			const contentId = this.titleListModule.editedContentId ? this.titleListModule.editedContentId : null;
			if (contentId && currentPath !== Paths.EditManageChapters) {
				this.$router.push(GLUtil.pathStringify(Paths.EditManageChapters, { id: contentId }));
			}
		} else {
			// if currently on manage chapter screen, then go to edit text
			const redirect = () => {
				if (chapterId !== "none" && this.titleListModule.isParent) {
					this.$router.replace(GLUtil.pathStringify(Paths.EditText, { id: this.$route.params.id }));
				}
			};

			// on chapter change if text has been changed, then show warning
			if (this.titleListModule.textPageDirty) {
				MessageHelper.Confirm(fmtMsg(TitleListLocale.TitleListLeaveWarn), () => {
					this.titleListModule.togglePageDirty();
					this.titleListModule.setEditeContentSelectedChapterId(chapterId);
					redirect();
				});
			} else {
				this.titleListModule.setEditeContentSelectedChapterId(chapterId);
				const chapter = this.titleListModule.editedContentDetails.chapters.find(
					(chapter) => chapter.id === chapterId
				);
				this.titleListModule.changeChapterContentInfo(chapter?.isInfo || false);
				redirect();
			}
		}
	}

	formValidated(rule: string, passed: boolean) {
		this.titleListModule.setValidForm(passed);
		this.titleListModule.setFormTocuched(true);
	}

	getFormDetails(): Promise<boolean | object> {
		const form = this.$refs.commonForm as FormModel;
		return new Promise((res) => {
			form.validate((isPassed) => {
				if (isPassed) {
					if (
						this.titleListModule.editedContentDetails.id &&
						this.titleListModule.editedContentDetails.id.length > 0 &&
						this.titleListModule.editeContentSelectedChapterId !== "none" &&
						this.titleListModule.editeContentSelectedChapterId !== "all"
					) {
						res({
							...this.contentDetails,
							chapterId: this.titleListModule.editeContentSelectedChapterId,
							chapterName: this.chapterName
						});
					} else {
						res(this.contentDetails);
					}
				} else {
					res(false);
				}
			});
		});
	}

	public selectControlContainer(element: HTMLDivElement) {
		return element.parentNode;
	}

	onChapterNameChange(e) {
		this.chapterName = e.target.value;

		if (!this.titleListModule.formTouched) {
			this.titleListModule.setFormTocuched(true);
		}

		this.titleListModule.setValidForm(!!(this.chapterName && this.chapterName.length));
	}

	isChapterNameDisabled() {
		const currentPath = this.$route.matched.slice(-1)[0].path;
		const enabled = currentPath === Paths.EditText; // Enable name edit only on edit text route.

		return !enabled;
	}

	get isReaderChapter() {
		return (
			this.titleListModule.editedContentDetails.languageSkillId === LanguageSkill.Read &&
			this.titleListModule.editeContentSelectedChapterId !== "all" &&
			this.titleListModule.editeContentSelectedChapterId !== "none"
		);
	}

	onIsContentInfoChange(e: HTMLInputElement & any) {
		// show warning to user everytime the checkbox is checked
		if (e.target.checked) {
			Modal.confirm({
				onOk: async () => {
					this.titleListModule.setFormTocuched(true);
					this.titleListModule.changeChapterContentInfo(true);

					this.titleListModule.textPageDirty && this.titleListModule.togglePageDirty();
				},
				title: fmtMsg(CommonFormLocale.CommonMessageIsContentInfoConfirmAreYouSure)
			});
		} else {
			Modal.confirm({
				onOk: async () => {
					this.titleListModule.setFormTocuched(true);
					this.titleListModule.changeChapterContentInfo(false);

					this.titleListModule.textPageDirty && this.titleListModule.togglePageDirty();
				},
				title: fmtMsg(CommonFormLocale.CommonMessageIsContentInfoConfirmRemoveAreYouSure)
			});
		}
	}
}
